/* eslint-disable camelcase */
import React, { useState } from "react";
import { Formik } from "formik";
import validator from "validator";

import {
  Form,
  Loader,
  Heading,
  Container,
  Row,
  Column,
  ActionBar,
  DetailsItem,
  TextArea,
  TextInput,
  SelectBox,
  ButtonGroup,
  SubmitButton,
  CancelButton,
  SwitchInput,
  PermissionsEdit,
  apiShape,
  intlShape,
  accessRightsShape,
  userShape,
  browserShape,
  notifierShape,
  withApi,
  withIntl,
  withUser,
  withBrowser,
  withFlipper,
  withNotifier,
  withAccessRights,
  handleFormikValueChange,
  capitalizeWords,
  removeNullAttributes,
  setEmptyOrUndefinedToNull,
  pullBooleanFromSpec,
  pullValueFromSpec,
  PermissionType,
  ContextualHelp,
} from "lcm-iot-commons";
import { FormattedMessage } from "react-intl";

import { EdgeDeviceTypes } from "./EdgeDeviceConstants";

import EdgeDeviceApplicationsEdit from "./EdgeDeviceApplicationsEdit";
import EdgeNotificationHandler from "./EdgeNotificationHandler";

type logLevel = { code: string; name: string };

const logLevelOptions: logLevel[] = [
  { code: "trace", name: "Trace" },
  { code: "debug", name: "Debug" },
  { code: "info", name: "Info" },
  { code: "warning", name: "Warning" },
  { code: "error", name: "Error" },
  { code: "critical", name: "Critical" },
  { code: "off", name: "Off" },
];

const extractEthernetipSupported = (response: $TSFixMe) =>
  response.software_version?.specifications
    ? pullBooleanFromSpec(response.software_version.specifications, [
        "ethernetip_supported",
      ])
    : false;

const extractProfinetSupported = (response: $TSFixMe) =>
  response.software_version?.specifications
    ? pullBooleanFromSpec(response.software_version.specifications, [
        "profinet_supported",
      ])
    : false;

const getCollectionIntervalValueSGC500 = (value: string) => {
  const result = convertMinutesToMillisecounds(value);
  return result !== 0 ? result : 10000;
};

interface SwitchSectionProps {
  intl: intlShape;
  id: string;
  labelId: string;
  hintHeadingId: string;
  hintTextId: string;
  switchId: string;
  switchName: string;
  formProps: Record<string, $TSFixMe>;
}

function SwitchSection({
  intl,
  id,
  labelId,
  hintHeadingId,
  hintTextId,
  switchId,
  switchName,
  formProps,
}: SwitchSectionProps): JSX.Element {
  return (
    <div>
      <h3 id={`${id}-switch-section`}>
        {intl.formatMessage({ id: labelId })}
        <ContextualHelp
          title={intl.formatMessage({ id: hintHeadingId })}
          interactive
        >
          <p>
            <FormattedMessage id={hintTextId} />
          </p>
        </ContextualHelp>
      </h3>
      <SwitchInput {...formProps} id={switchId} name={switchName} />
    </div>
  );
}

export const convertSecondsToMinutes = (sec: number) =>
  sec ? Math.round(sec / 60) : null;
export const convertMinutesToSeconds = (min: string) =>
  min ? parseInt(min, 10) * 60 : null;

export const convertMillisecoundsToMinutes = (ms: number) =>
  ms ? Math.round(ms / 1000 / 60) : null;
export const convertMinutesToMillisecounds = (min: string) =>
  min ? parseInt(min, 10) * 60 * 1000 : null;

export const extractInitialValues = (response: $TSFixMe) => {
  const typeCode = response.type?.code;
  const { specifications } = response;

  return {
    id: response.id,
    type: response.type,
    serial_number: response.serial_number,
    name: response.name,
    description: response.description,
    is_sgc200: typeCode === EdgeDeviceTypes.SGC200,
    is_sgc500:
      typeCode === EdgeDeviceTypes.SGC500_AA ||
      typeCode === EdgeDeviceTypes.SGC500_AB ||
      typeCode === EdgeDeviceTypes.SGC500_AB_24 ||
      typeCode === EdgeDeviceTypes.SGC500_AC ||
      typeCode === EdgeDeviceTypes.SGC500_SW,
    has_api_subscription: "api_subscription" in response,
    client_application: response.api_subscription?.client_application?.name,
    ethernetip_supported: extractEthernetipSupported(response),
    ethernet_ip_active: pullBooleanFromSpec(specifications, [
      "eh.user_config.ethernet_ip_active",
    ]),
    profinet_supported: extractProfinetSupported(response),
    profinet_active: pullBooleanFromSpec(specifications, [
      "eh.user_config.profinet_active",
    ]),
    netilion_health_active:
      pullBooleanFromSpec(specifications, [
        "eh.user_config.feature.netilion_health_active",
      ]) ?? true,
    netilion_value_active:
      pullBooleanFromSpec(specifications, [
        "eh.user_config.feature.netilion_value_active",
      ]) ?? true,
    // values arrive in seconds
    data_collection_interval: convertSecondsToMinutes(
      pullValueFromSpec(specifications, [
        "eh.user_config.data_collection_interval",
      ]),
    )?.toString(),
    data_send_interval: convertSecondsToMinutes(
      pullValueFromSpec(specifications, ["eh.user_config.data_send_interval"]),
    )?.toString(),
    health_data_collection_interval: convertMillisecoundsToMinutes(
      pullValueFromSpec(specifications, [
        "eh.user_config.settings.health.dvmonitor_interval",
      ]),
    )?.toString(),
    measurement_value_data_collection_interval: convertMillisecoundsToMinutes(
      pullValueFromSpec(specifications, [
        "eh.user_config.settings.valueservice.dvmonitor_interval",
      ]),
    )?.toString(),
    log_level: {
      code: response.log_level,
      name: capitalizeWords(response.log_level),
    },
    is_third_party_type: typeCode === "third_party_edge_device",
  };
};

export const createPayload = (values: $TSFixMe) => {
  const {
    name,
    description,
    log_level,
    is_sgc200,
    is_sgc500,
    has_api_subscription,
    data_collection_interval,
    data_send_interval,
    health_data_collection_interval,
    measurement_value_data_collection_interval,
    ethernetip_supported,
    ethernet_ip_active,
    profinet_supported,
    profinet_active,
    netilion_health_active,
    netilion_value_active,
  } = values;
  const payload = {
    name: name?.trim(),
    description: description?.trim(),
    log_level: log_level.code,
  };
  let specifications: $TSFixMe = {};

  if (is_sgc200) {
    specifications = {
      ...specifications,
      "eh.user_config.data_collection_interval": {
        value: convertMinutesToSeconds(data_collection_interval)?.toString(),
      },
      "eh.user_config.data_send_interval": {
        value: convertMinutesToSeconds(data_send_interval)?.toString(),
      },
    };
  } else if (is_sgc500) {
    specifications = {
      ...specifications,
      "eh.user_config.settings.health.dvmonitor_interval": {
        value: getCollectionIntervalValueSGC500(
          health_data_collection_interval,
        )?.toString(),
      },
      "eh.user_config.settings.valueservice.dvmonitor_interval": {
        value: getCollectionIntervalValueSGC500(
          measurement_value_data_collection_interval,
        )?.toString(),
      },
    };
  }
  if (ethernetip_supported) {
    specifications = {
      ...specifications,
      "eh.user_config.ethernet_ip_active": {
        value: ethernet_ip_active ? ethernet_ip_active.toString() : "false",
      },
    };
  }
  if (profinet_supported) {
    specifications = {
      ...specifications,
      "eh.user_config.profinet_active": {
        value: profinet_active ? profinet_active.toString() : "false",
      },
    };
  }
  if (is_sgc500 && !has_api_subscription) {
    specifications = {
      ...specifications,
      "eh.user_config.feature.netilion_health_active": {
        value: netilion_health_active?.toString() ?? "true",
      },
      "eh.user_config.feature.netilion_value_active": {
        value: netilion_value_active?.toString() ?? "true",
      },
    };
  }

  if (Object.keys(specifications).length > 0) {
    (payload as $TSFixMe).specifications = specifications;
  }

  return setEmptyOrUndefinedToNull(payload);
};

export const validateAccessRights = (accessRights: $TSFixMe) =>
  accessRights.canUpdate;

type OwnEdgeDeviceEditProps = {
  api: apiShape;
  intl: intlShape;
  browser: browserShape;
  notifier: notifierShape;
  accessRights: accessRightsShape;
  user: userShape;
  match: {
    params?: {
      id?: string;
    };
  };
  flipper: {
    edgeDeviceDataCollectionInterval?: boolean;
    edgeDeviceActiveFeatures?: boolean;
  };
};

export function EdgeDeviceEdit(props: OwnEdgeDeviceEditProps) {
  const { api, intl, browser, match, notifier, accessRights, flipper, user } =
    props;
  // @ts-expect-error TS(2339) FIXME: Property 'id' does not exist on type '{ id?: strin... Remove this comment to see the full error message
  const { id } = match.params;
  const [initialValues, setInitialValues] = useState();
  const edgeDeviceAdminRole = (user as $TSFixMe)?.userRoles.find(
    (role: $TSFixMe) => role.name === "edge_device_admin",
  );
  const notEdgeDeviceAdmin = !edgeDeviceAdminRole;

  const loadData = async () => {
    try {
      const response = await api.get(
        `/edm/edge_devices/${id}`,
        {
          include:
            "type,specifications,software_version,software_version.specifications",
        },
        false,
      );
      // @ts-expect-error TS(2345) FIXME: Argument of type '{}' is not assignable to paramet... Remove this comment to see the full error message
      setInitialValues(extractInitialValues(response));
    } catch (error) {
      notifier.showError(api.translateError(error));
      // @ts-expect-error TS(2345) FIXME: Argument of type '{}' is not assignable to paramet... Remove this comment to see the full error message
      setInitialValues({});
    }
  };

  React.useEffect(() => {
    loadData();
  }, [id]);

  const onSubmit = async (values: $TSFixMe, actions: $TSFixMe) => {
    const payload = createPayload(values);
    try {
      await api.patch(`/edm/edge_devices/${id}`, payload);
      /* istanbul ignore else */
      if (payload.specifications) {
        await api.patch(
          `/edm/edge_devices/${id}/specifications`,
          payload.specifications,
        );
      }
      notifier.showSuccess(
        intl.formatMessage({ id: "edge_device_edit.success_notification" }),
      );
      browser.goBack();
    } catch (error) {
      notifier.showError(api.translateError(error));
    } finally {
      actions.setSubmitting(false);
    }
  };

  const validateName = (name: string) => {
    if (!name) return null;
    return validator.isLength(name, { max: 255 })
      ? null
      : intl.formatMessage({ id: "validation.too_long" }, { length: 255 });
  };

  const validateWholeNumber = (value: string) => {
    if (!value) return null;
    return validator.isInt(value, { min: 0 })
      ? null
      : intl.formatMessage({ id: "validation.whole_number" });
  };

  const validateDataSendInterval = (interval: string) => {
    if (!interval) return null;
    return validator.isInt(interval, { min: 15 })
      ? null
      : intl.formatMessage({
          id: "edge_device.validation.send_interval_to_small",
        });
  };

  const validateForm = (values: $TSFixMe) => {
    type Errors = {
      name?: $TSFixMe;
      data_collection_interval: string | null;
      data_send_interval?: string | null;
      health_data_collection_interval: string | null;
      measurement_value_data_collection_interval: string | null;
    };
    const {
      name,
      data_collection_interval,
      data_send_interval,
      health_data_collection_interval,
      measurement_value_data_collection_interval,
      is_sgc200,
      is_sgc500,
    } = values;
    const errors: Errors = {
      data_collection_interval: null,
      data_send_interval: null,
      health_data_collection_interval: null,
      measurement_value_data_collection_interval: null,
    };
    errors.name = validateName(name);
    if (is_sgc200) {
      errors.data_send_interval = validateDataSendInterval(data_send_interval);
      errors.data_collection_interval = validateWholeNumber(
        data_collection_interval,
      );
    }
    if (is_sgc200 && data_collection_interval && data_send_interval) {
      errors.data_collection_interval = validator.isInt(
        data_collection_interval,
        { min: 0, max: parseInt(data_send_interval, 10) },
      )
        ? null
        : intl.formatMessage({
            id: "edge_device.validation.send_vs_collection",
          });
    }
    if (is_sgc500) {
      errors.health_data_collection_interval = validateWholeNumber(
        health_data_collection_interval,
      );
      errors.measurement_value_data_collection_interval = validateWholeNumber(
        measurement_value_data_collection_interval,
      );
    }
    return removeNullAttributes(errors);
  };

  const renderForm = (formProps: $TSFixMe) => {
    const { isSubmitting, values } = formProps;
    const {
      is_sgc200,
      is_sgc500,
      ethernetip_supported,
      profinet_supported,
      client_application,
      serial_number,
      is_third_party_type,
    } = values;
    return (
      <Form {...formProps}>
        <DetailsItem
          id="serial-number"
          translationKey="label.serial_number"
          value={serial_number}
        />
        {client_application && (
          <DetailsItem
            id="netilion-connect-client-application-name"
            translationKey="label.connect_client_application"
            value={client_application}
          />
        )}
        {is_third_party_type ? null : (
          <SelectBox
            {...formProps}
            id="log-level"
            name="log_level"
            valueKey="code"
            labelKey="name"
            label={intl.formatMessage({ id: "label.log_level" })}
            options={logLevelOptions}
            handleChange={(event) =>
              handleFormikValueChange(
                { ...formProps, name: "log_level" },
                event.target.value,
              )
            }
            disabled={notEdgeDeviceAdmin}
          />
        )}
        <TextInput
          {...formProps}
          id="name"
          name="name"
          label={intl.formatMessage({ id: "label.name" })}
        />
        <TextArea
          {...formProps}
          id="description"
          name="description"
          label={intl.formatMessage({ id: "label.description" })}
        />
        {is_sgc200 && (
          <div>
            <h2 id="connectivity_settings-header">
              {intl.formatMessage({
                id: "edge_devices.connectivity_settings",
              })}
            </h2>
            <TextInput
              {...formProps}
              id="data-collection-interval"
              name="data_collection_interval"
              label={intl.formatMessage({
                id: "label.data_collection_interval",
              })}
              placeholder={intl.formatMessage({ id: "label.minutes" })}
            />
            <TextInput
              {...formProps}
              id="data-send-interval"
              name="data_send_interval"
              label={intl.formatMessage({ id: "label.data_send_interval" })}
              placeholder={intl.formatMessage({ id: "label.minutes" })}
            />
          </div>
        )}
        {(ethernetip_supported || profinet_supported) && (
          <div>
            <h2 id="connectivity_settings-header">
              {intl.formatMessage({
                id: "edge_devices.connectivity_settings",
              })}
            </h2>
          </div>
        )}
        {ethernetip_supported && (
          <SwitchSection
            intl={intl}
            id="eip"
            labelId="label.eip.activation_status"
            hintHeadingId="edge_device_edit.connectivity_settings.ethernet_ip.hint_heading"
            hintTextId="edge_device_edit.connectivity_settings.ethernet_ip.hint_text"
            switchId="eip-switch"
            switchName="ethernet_ip_active"
            formProps={formProps}
          />
        )}
        {profinet_supported && (
          <SwitchSection
            intl={intl}
            id="profinet"
            labelId="label.profinet.activation_status"
            hintHeadingId="edge_device_edit.connectivity_settings.profinet.hint_heading"
            hintTextId="edge_device_edit.connectivity_settings.profinet.hint_text"
            switchId="profinet-switch"
            switchName="profinet_active"
            formProps={formProps}
          />
        )}
        {is_sgc500 &&
          initialValues &&
          !(initialValues as $TSFixMe).has_api_subscription &&
          flipper?.edgeDeviceActiveFeatures && (
            <div>
              <h2 id="data_collection-header">
                {intl.formatMessage({ id: "label.data_collection" })}
              </h2>
              <SwitchSection
                intl={intl}
                id="netilion_health"
                labelId="label.netilion_health.activation_status"
                hintHeadingId="edge_device_edit.data_collection.netilion_health.hint_heading"
                hintTextId="edge_device_edit.data_collection.netilion_health.hint_text"
                switchId="netilion_health-switch"
                switchName="netilion_health_active"
                formProps={formProps}
              />
              <SwitchSection
                intl={intl}
                id="netilion_value"
                labelId="label.netilion_value.activation_status"
                hintHeadingId="edge_device_edit.data_collection.netilion_value.hint_heading"
                hintTextId="edge_device_edit.data_collection.netilion_value.hint_text"
                switchId="netilion_value-switch"
                switchName="netilion_value_active"
                formProps={formProps}
              />
            </div>
          )}
        {is_sgc500 && flipper?.edgeDeviceDataCollectionInterval && (
          <div>
            <h2 id="data_collection_intervals-header">
              {intl.formatMessage({ id: "label.data_collection_interval" })}
            </h2>
            <TextInput
              {...formProps}
              id="health-data-collection-interval"
              name="health_data_collection_interval"
              label={intl.formatMessage({
                id: "label.health_data_collection_interval",
              })}
              placeholder={intl.formatMessage({ id: "label.minutes" })}
              help="true"
              helpTitle={intl.formatMessage({
                id: "edge_device_edit.connectivity_settings.health_data_collection_interval.hint_heading",
              })}
              helpText={`${intl.formatMessage({
                id: "edge_device_edit.connectivity_settings.health_data_collection_interval.hint_text",
              })}\n${intl.formatMessage({
                id: "edge_device_edit.connectivity_settings.health_data_collection_interval.hint_text2",
              })}`}
            />
            <TextInput
              {...formProps}
              id="measurement-value-data-collection-interval"
              name="measurement_value_data_collection_interval"
              label={intl.formatMessage({
                id: "label.measurement_value_data_collection_interval",
              })}
              placeholder={intl.formatMessage({ id: "label.minutes" })}
              help="true"
              helpTitle={intl.formatMessage({
                id: "edge_device_edit.connectivity_settings.measurement_value_data_collection_interval.hint_heading",
              })}
              helpText={`${intl.formatMessage({
                id: "edge_device_edit.connectivity_settings.measurement_value_data_collection_interval.hint_text",
              })}\n${intl.formatMessage({
                id: "edge_device_edit.connectivity_settings.measurement_value_data_collection_interval.hint_text2",
              })}`}
            />
          </div>
        )}
        <div className="space-after" />
        <ButtonGroup>
          <SubmitButton
            id="edit-submit-button"
            fetching={!initialValues || isSubmitting}
            disabled={!formProps.dirty || isSubmitting}
          />
          <CancelButton
            id="edit-cancel-button"
            fetching={!initialValues || isSubmitting}
            // @ts-expect-error TS(2322) FIXME: Type '{ id: string; fetching: true; disabled: any;... Remove this comment to see the full error message
            disabled={isSubmitting}
          />
        </ButtonGroup>
      </Form>
    );
  };

  return (
    <Container>
      <Row>
        <Column>
          <ActionBar>
            <Heading
              id="edit-edge-device-header"
              title={intl.formatMessage({ id: "edge_device_edit.header" })}
            />
          </ActionBar>
        </Column>
      </Row>
      {initialValues && (
        <Row>
          <Column>
            <Formik
              id="edge-device-edit-form"
              onSubmit={onSubmit}
              validate={validateForm}
              initialValues={initialValues}
            >
              {renderForm}
            </Formik>
          </Column>
        </Row>
      )}
      {(initialValues as $TSFixMe)?.is_third_party_type ? null : (
        <Row>
          <Column sm="12">
            <EdgeNotificationHandler edgeId={parseInt(id, 10)} />
          </Column>
        </Row>
      )}
      {(initialValues as $TSFixMe)?.is_third_party_type ? null : (
        <Row>
          <Column sm="12">
            {initialValues &&
              (initialValues as $TSFixMe).has_api_subscription && (
                <EdgeDeviceApplicationsEdit edgeDevice={initialValues} />
              )}
          </Column>
        </Row>
      )}
      <Row>
        <Column sm="12">
          {initialValues && (
            <PermissionsEdit
              // @ts-expect-error TS(2322) FIXME: Type '{ id: string; permitableType: string; permit... Remove this comment to see the full error message
              id="edm-edgedevice-edit-permissions"
              permitableType="EDM::EdgeDevice"
              permitableId={(initialValues as $TSFixMe).id}
              accessRights={accessRights}
              allowedPermissions={[PermissionType.READ, PermissionType.UPDATE]}
            />
          )}
        </Column>
      </Row>
      <Loader loading={!initialValues} />
    </Container>
  );
}

export default withUser(
  withBrowser(
    withApi(
      withIntl(
        withFlipper(
          withNotifier(
            withAccessRights(
              EdgeDeviceEdit,
              "EDM::EdgeDevice",
              validateAccessRights,
            ),
          ),
        ),
      ),
    ),
  ),
);
